import type { FormProps } from "../components/layout/bottom-sheet";

/**
 * フォーム構造と入力値から顧客名を抽出する。
 * 内部フィールド名ではなく、姓/名ラベルや名前関連の質問名を基に判定する。
 */
export function buildCustomerName(
  values: Record<string, string>,
  formProps: FormProps | null,
): string {
  if (formProps) {
    const nameCandidates: Array<{ priority: number; value: string }> = [];

    formProps.page.forEach((page) => {
      page.list.forEach((entry) => {
        const formName = entry.form_name || "";
        const normalizedFormName = formName.replace(/\s+/g, "");
        const isNameEntry =
          normalizedFormName.includes("名前") || normalizedFormName.includes("氏名");
        if (!isNameEntry) return;

        const seiField = entry.fields.find(
          (field) => field.label === "姓" || field.name.endsWith("-sei"),
        );
        const meiField = entry.fields.find(
          (field) => field.label === "名" || field.name.endsWith("-mei"),
        );
        const seiValue = seiField ? (values[seiField.name] || "").trim() : "";
        const meiValue = meiField ? (values[meiField.name] || "").trim() : "";

        if (seiValue && meiValue) {
          const priority = normalizedFormName.includes("漢字")
            ? 3
            : normalizedFormName.includes("カナ")
              ? 1
              : 2;
          nameCandidates.push({ priority, value: `${seiValue} ${meiValue}` });
          return;
        }

        const singleField = entry.fields[0];
        if (singleField) {
          const singleValue = (values[singleField.name] || "").trim();
          if (singleValue) {
            const priority = normalizedFormName.includes("漢字")
              ? 3
              : normalizedFormName.includes("カナ")
                ? 1
                : 2;
            nameCandidates.push({ priority, value: singleValue });
          }
        }
      });
    });

    if (nameCandidates.length > 0) {
      return nameCandidates.sort((a, b) => b.priority - a.priority)[0].value;
    }
  }

  // フォールバック: よく使われるフィールド名でチェック
  const kanjiSei = values["お名前-漢字-sei"] || values["name-sei"];
  const kanjiMei = values["お名前-漢字-mei"] || values["name-mei"];
  if (kanjiSei && kanjiMei) return `${kanjiSei} ${kanjiMei}`;
  const kanaSei = values["お名前-カナ-sei"];
  const kanaMei = values["お名前-カナ-mei"];
  if (kanaSei && kanaMei) return `${kanaSei} ${kanaMei}`;
  return values["お名前"] || values["name"] || "";
}

/**
 * フォーム構造と入力値からメールアドレスと電話番号を抽出する。
 */
export function buildCustomerContact(
  values: Record<string, string>,
  formProps: FormProps | null,
): { mail: string; phone: string } {
  if (formProps) {
    let mail = "";
    let phone = "";

    formProps.page.forEach((page) => {
      page.list.forEach((entry) => {
        entry.fields.forEach((field) => {
          const value = (values[field.name] || "").trim();
          if (!value) return;
          if (!mail && field.type === "email") {
            mail = value;
            return;
          }
          if (!phone && field.type === "tel") {
            phone = value;
          }
        });
      });
    });

    if (mail || phone) return { mail, phone };
  }

  // フォールバック: よく使われるフィールド名でチェック
  const mail =
    values["メールアドレス"] ||
    values["メールアドレス-確認"] ||
    values["email-confirm"] ||
    values["email"] ||
    "";
  const phone = values["電話番号"] || values["tel"] || values["phone"] || "";
  return { mail, phone };
}

/**
 * フォーム構造と入力値から送信用ペイロードを生成する。
 * 内部フィールド名ではなく、管理画面で設定した質問名をキーとして使用する。
 */
export function buildSubmissionPayload(
  values: Record<string, string>,
  formProps: FormProps | null,
): Record<string, string> {
  if (!formProps) return { ...values };

  const payload: Record<string, string> = {};
  const usedKeys = new Set<string>();

  const assignValue = (key: string, value: string) => {
    const trimmedKey = key.trim();
    const trimmedValue = value.trim();
    if (!trimmedKey || !trimmedValue) return;

    let nextKey = trimmedKey;
    let suffix = 2;
    while (usedKeys.has(nextKey)) {
      nextKey = `${trimmedKey}(${suffix})`;
      suffix++;
    }

    usedKeys.add(nextKey);
    payload[nextKey] = trimmedValue;
  };

  formProps.page.forEach((page) => {
    page.list.forEach((entry) => {
      const entryTitle = (entry.form_name || "").trim();
      const filledFields = entry.fields
        .map((field) => ({ field, value: (values[field.name] || "").trim() }))
        .filter((item) => item.value);

      if (filledFields.length === 0) return;

      // 姓/名ペアは結合して一つのキーにする
      const isNamePair =
        filledFields.length >= 2 &&
        filledFields.every(
          (item) => item.field.label === "姓" || item.field.label === "名",
        );

      if (isNamePair) {
        const seiValue = filledFields.find((item) => item.field.label === "姓")?.value || "";
        const meiValue = filledFields.find((item) => item.field.label === "名")?.value || "";
        const combinedValue = [seiValue, meiValue].filter(Boolean).join(" ");
        assignValue(entryTitle || "お名前", combinedValue);
        return;
      }

      if (filledFields.length === 1) {
        const onlyField = filledFields[0];
        assignValue(
          entryTitle || onlyField.field.label || onlyField.field.name,
          onlyField.value,
        );
        return;
      }

      filledFields.forEach(({ field, value }) => {
        const detailedTitle =
          entryTitle && field.label
            ? `${entryTitle}（${field.label}）`
            : entryTitle || field.label || field.name;
        assignValue(detailedTitle, value);
      });
    });
  });

  return payload;
}
